import { formatDateTime, formatMoneyCents } from '../lib/format';

/**
 * La hoja de la factura: the printed sheet itself, and nothing around it.
 *
 * Extracted so the owner's print view and the customer's public page render the
 * SAME document from the same markup. A fiscal document that looks different
 * depending on who opened it is a bug the day somebody compares two printouts,
 * and two copies of this JSX would drift on the first edit.
 *
 * The styles live in `src/styles/factura.css` and are keyed off these class
 * names, including the print rules.
 */

export type FacturaSheetLine = {
  name: string;
  quantity: number;
  lineTotalCents: number;
};

export type FacturaSheetEmisor = {
  razonSocial: string;
  ruc: string;
  dv: string;
  direccion: string;
};

export type FacturaSheetData = {
  /** Null while the owner has not configured the perfil fiscal yet. */
  emisor: FacturaSheetEmisor | null;
  displayNumber: string;
  status: 'emitida' | 'anulada';
  issuedAt: string;
  clientName: string | null;
  clientRuc: string | null;
  clientDv: string | null;
  subtotalCents: number;
  discountCents: number;
  itbmsRateBp: number;
  itbmsCents: number;
  totalCents: number;
  voidReason: string | null;
  /** Null when the detail could not be resolved; see `linesPending`. */
  lines: FacturaSheetLine[] | null;
  /** True while the detail is still loading, so the sheet says so rather than lying. */
  linesPending?: boolean;
};

/** ITBMS basis points as the printed percent label ('700' -> '7%'). */
export function itbmsLabel(rateBp: number): string {
  return `${rateBp % 100 === 0 ? rateBp / 100 : (rateBp / 100).toFixed(2)}%`;
}

export function FacturaSheet({ factura }: { factura: FacturaSheetData }) {
  const anulada = factura.status === 'anulada';
  const taxableBaseCents = factura.subtotalCents - factura.discountCents;

  return (
    <article className="factura-sheet" aria-label={`Factura ${factura.displayNumber}`}>
      <header className="factura-head">
        <div className="factura-emisor">
          <p className="razon">{factura.emisor?.razonSocial ?? 'Tu negocio'}</p>
          {factura.emisor ? (
            <p className="fiscal">
              RUC {factura.emisor.ruc} DV {factura.emisor.dv}
              <br />
              {factura.emisor.direccion}
            </p>
          ) : null}
        </div>
        <div className="factura-id">
          <p className="kind">Factura</p>
          <p className="numero">{factura.displayNumber}</p>
          <p className="fecha">{formatDateTime(factura.issuedAt)}</p>
          {anulada ? <p className="anulada">Anulada</p> : null}
        </div>
      </header>

      {factura.clientName || factura.clientRuc ? (
        <div className="factura-cliente">
          <p className="label">Facturado a</p>
          {factura.clientName ? <p>{factura.clientName}</p> : null}
          {factura.clientRuc ? (
            <p>
              RUC {factura.clientRuc}
              {factura.clientDv ? ` DV ${factura.clientDv}` : ''}
            </p>
          ) : null}
        </div>
      ) : null}

      <table className="factura-lineas">
        <thead>
          <tr>
            <th scope="col">Detalle</th>
            <th scope="col" className="num">
              Cant.
            </th>
            <th scope="col" className="num">
              Importe
            </th>
          </tr>
        </thead>
        <tbody>
          {factura.lines && factura.lines.length > 0 ? (
            factura.lines.map((line, index) => (
              <tr key={`${line.name}-${index}`}>
                <td>{line.name}</td>
                <td className="num">{line.quantity}</td>
                <td className="num">{formatMoneyCents(line.lineTotalCents)}</td>
              </tr>
            ))
          ) : factura.linesPending ? (
            <tr>
              <td>Cargando detalle…</td>
              <td className="num" />
              <td className="num" />
            </tr>
          ) : (
            // Says it plainly instead of printing an invented "Servicios
            // prestados" line: a wrong detail on a fiscal document is worse
            // than an honest gap.
            <tr>
              <td>Detalle no disponible</td>
              <td className="num">1</td>
              <td className="num">{formatMoneyCents(factura.subtotalCents)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="factura-totales">
        <p className="linea">
          Subtotal <span className="monto">{formatMoneyCents(factura.subtotalCents)}</span>
        </p>
        {factura.discountCents > 0 ? (
          <p className="linea">
            Descuento <span className="monto">−{formatMoneyCents(factura.discountCents)}</span>
          </p>
        ) : null}
        {factura.discountCents > 0 ? (
          <p className="linea">
            Base imponible <span className="monto">{formatMoneyCents(taxableBaseCents)}</span>
          </p>
        ) : null}
        <p className="linea">
          ITBMS ({itbmsLabel(factura.itbmsRateBp)}){' '}
          <span className="monto">{formatMoneyCents(factura.itbmsCents)}</span>
        </p>
        <p className="linea total">
          Total <span className="monto">{formatMoneyCents(factura.totalCents)}</span>
        </p>
      </div>

      <footer className="factura-pie">
        <span>
          {anulada && factura.voidReason
            ? `Anulada: ${factura.voidReason}`
            : 'Gracias por su visita.'}
        </span>
        <span>Documento generado por Dale Negocios</span>
      </footer>
    </article>
  );
}
